import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { times } from 'lodash-es'
import { FunctionComponent, useMemo } from 'react'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { WithClassName } from '~/types/utils'
import { Icon } from './Icon'
import { InputTextWrapper, NativeInput } from './Input'
import { UnstyledSelect } from './Select'

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const PageButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`

const PageButton = styled.div<{ active?: boolean; disabled?: boolean }>`
  user-select: none;
  padding: 0.5rem;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 1rem;
  max-width: 1rem;
  height: 1rem;
  ${Fonts.P1}
  font-weight: 400;

  ${({ active, disabled }) =>
    disabled
      ? css`
          color: ${Colors.Silver};
        `
      : active
        ? css`
            color: ${Colors.White};
            background-color: ${Colors.King};
          `
        : css`
            color: ${Colors.Jet};
            cursor: pointer;
            ${Icon} {
              color: ${Colors.King};
            }
         `}
  

  :hover {
    ${({ active, disabled }) =>
      !active &&
      !disabled &&
      css`
        background-color: ${Colors.Topaze};
      `}
  }
`

const FixedWidthDiv = styled.div`
  width: 300px;
`

const LimitSelector = styled(FixedWidthDiv)`
  color: ${Colors.SlateGrey};
  ${Fonts.P2}

  ${InputTextWrapper} {
    background: transparent;
  }

  ${NativeInput} {
    ${Fonts.colors.King}
    font-weight: 500;
    width: 20px;
    height: 1rem;
    background: transparent;
  }

  fieldset {
    border: none !important;
  }

  span {
    svg {
      fill: ${Colors.King}
    }
  }
`

const ItemsCount = styled(FixedWidthDiv)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-align: right;
  color: ${Colors.SlateGrey};
  ${Fonts.P2}
`

// Constants
export const PAGE_LIMIT_OPTIONS = [10, 25, 50]
const MAX_BUTTONS = 7
const DISPLAYED_PAGES_COUNT = 4

type Props = WithClassName & {
  currentPage: number
  totalPages: number
  limit: number
  totalItems: number
  showPageButtons?: boolean
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
}

const BasePagination: FunctionComponent<Props> = ({ className, currentPage, totalPages, limit, totalItems, showPageButtons = true, onPageChange, onLimitChange }) => {
  const pageNumbers = useMemo<('...' | number)[]>(() => {
    if (!showPageButtons) return []

    const pageNumbers: ('...' | number)[] = []

    // Populate `pageNumbers` array based on total pages
    if (totalPages <= MAX_BUTTONS) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i)
      }
    } else {
      // Add first page
      pageNumbers.push(1)

      if (currentPage <= DISPLAYED_PAGES_COUNT) {
        // Add first pages
        times(DISPLAYED_PAGES_COUNT, (e) => pageNumbers.push(e + 2))

        // Add an ellipsis
        pageNumbers.push('...')
      } else if (currentPage > totalPages - DISPLAYED_PAGES_COUNT) {
        // Add an ellipsis
        pageNumbers.push('...')

        // Add last pages
        times(DISPLAYED_PAGES_COUNT, (e) => pageNumbers.push(totalPages - DISPLAYED_PAGES_COUNT + e))
      } else {
        // Add an ellipsis
        pageNumbers.push('...')

        // Add page before current page
        pageNumbers.push(currentPage - 1)

        // Add current page
        pageNumbers.push(currentPage)

        // Add page after current page
        pageNumbers.push(currentPage + 1)

        // Add an ellipsis
        pageNumbers.push('...')
      }

      // Add last page
      pageNumbers.push(totalPages)
    }

    return pageNumbers
  }, [currentPage, totalPages, showPageButtons])

  if (totalPages === 0) return null

  return (
    <Container className={className}>
      <LimitSelector>
        <UnstyledSelect
          label=""
          value={limit}
          onChange={(e) => onLimitChange(e.value as number)}
          options={PAGE_LIMIT_OPTIONS.map((value) => ({ value, label: value.toString(), trackingLabel: 'common-pagination-limit-apply' }))}
        />
        <span>items per page</span>
      </LimitSelector>
      {showPageButtons && (
        <PageButtons>
          <PageButton onClick={() => currentPage !== 1 && onPageChange(currentPage - 1)} disabled={currentPage === 1}>
            <Icon name="chevron_left" height="1.25rem" />
          </PageButton>
          {pageNumbers.map((page, index) =>
            page === '...' ? (
              <PageButton key={`${page}-${index}`} disabled>
                ...
              </PageButton>
            ) : (
              <PageButton key={`${page}-${index}`} active={page === currentPage} onClick={() => onPageChange(page)}>
                {page}
              </PageButton>
            )
          )}
          <PageButton onClick={() => currentPage !== totalPages && onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            <Icon name="chevron_right" height="1.25rem" />
          </PageButton>
        </PageButtons>
      )}
      <ItemsCount>
        Showing {(currentPage - 1) * limit + 1} - {Math.min(currentPage * limit, totalItems)} of {totalItems}
        {!showPageButtons && (
          <>
            <PageButton onClick={() => currentPage !== 1 && onPageChange(currentPage - 1)} disabled={currentPage === 1}>
              <Icon name="chevron_left" height="1.25rem" />
            </PageButton>
            <PageButton onClick={() => currentPage !== totalPages && onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
              <Icon name="chevron_right" height="1.25rem" />
            </PageButton>
          </>
        )}
      </ItemsCount>
    </Container>
  )
}

export const Pagination = styled(BasePagination)``
